import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { contractsApi, CONTRACT_STATUS_LABELS } from '../api/contracts'
import { fetchMe } from '../api/auth'
import { myWorkQueueApi, type WorkQueueItem, type WorkQueueResponse } from '../api/workQueue'
import { Button, Callout, DataTable, EmptyState, FilterBar, LoadingState, Page, PageHeader, Select, StatusPill } from '../components/ui'
import { usePrompt } from '../components/ui'
import { PriorityWeightsPanel } from '../components/PriorityWeightsPanel'

// F-057 (REQ-212-001, REQ-213-003, REQ-214-001): "La mia coda di lavoro". Mostra le voci che il server
// assegna al ruolo di chi guarda, in ordine di priorità; non scrive nulla. Nessun importo, mai: il
// backend li toglie a chi non può vederli e questa pagina non li legge nemmeno ("il report comanda").

const TYPE_LABELS: Record<string, string> = {
  contract_da_verificare: 'Pratica da verificare',
  contract_integrazione_richiesta: 'Integrazione richiesta',
  contract_pronta_per_lo_scarico: 'Pratica pronta per lo scarico',
  contract_bozza_da_completare: 'Bozza da completare',
  contract_da_approvare: 'Pratica da approvare',
  contract_da_caricare_sul_portale: 'Da caricare sul portale',
  contract_esito_operatore_in_ritardo: 'Esito operatore in ritardo',
  // F-141 (REQ-213-003): solo con WorkQueue:Backoffice:DraftsAndBatches acceso sul server.
  contract_bozza_operatore_da_verificare: 'Bozza operatore da verificare',
  batch_da_elaborare: 'Lotto da elaborare',
  batch_da_approvare: 'Distinta da approvare',
  operator_outcome_anomalia: 'Anomalia import esiti operatore',
  lead_richiamata_da_effettuare: 'Richiamata da effettuare',
  lead_nuovo: 'Lead nuovo',
  lead_fuori_tempo_risposta: 'Tempo di risposta superato',
  lead_ritentativo_dovuto: 'Ritentativo dovuto',
  lead_tentativi_esauriti: 'Tentativi esauriti',
  invoice_da_incassare: 'Fattura da incassare',
  movement_da_riconciliare: 'Movimento da riconciliare',
  expense_da_approvare: 'Spesa da approvare',
  expense_in_scadenza: 'Pagamento in scadenza',
  movement_non_classificato: 'Movimento non classificato',
  expense_duplicata: 'Spese duplicate',
  partner_oltre_soglia: 'Socio oltre soglia',
  task_da_completare: 'Task da completare',
  appointment_da_gestire: 'Appuntamento da gestire',
  // C-03: voci del commerciale.
  trattativa_ferma: 'Trattativa ferma',
  preventivo_da_seguire: 'Preventivo da seguire',
  // R3-07: solo con il flag coda.fonti_estese.
  cliente_da_richiamare: 'Cliente da richiamare',
  cross_sell_opportunita: 'Opportunità di cross-selling',
  appointment_da_confermare: 'Appuntamento da confermare',
  // F-106 (REQ-213-004, REQ-213-005): Amministrazione e Direzione.
  credito_scaduto: 'Credito scaduto',
  incasso_differente: 'Incasso differente dal previsto',
  budget_superato: 'Budget superato',
  scadenza_imposta: 'Scadenza d’imposta',
  uscita_scaduta: 'Uscita scaduta',
  eccezioni_pratiche: 'Eccezioni delle pratiche',
  obiettivo_fuori_target: 'Obiettivo fuori target',
  anomalia_finanziaria: 'Anomalia finanziaria',
  alert_liquidita: 'Alert di liquidità',
  pratiche_ferme_sla: 'Pratiche oltre SLA',
  // F-118: errori di sistema, solo per l'Admin.
  error: 'Errore di sistema',
}

/** Tipo che solo l'Admin vede (e solo se il server ha acceso WorkQueue:ErrorItems:Enabled). */
const ERROR_TYPE = 'error'

/** Valore del filtro che non è un tipo di voce: "mostrami solo le pratiche ferme". */
const STALE_FILTER = 'contract_ferma'

const ROLE_LABELS: Record<string, string> = {
  Commerciale: 'Commerciale',
  CallCenter: 'Call center',
  BackOffice: 'Backoffice',
  Amministrazione: 'Amministrazione',
  Direzione: 'Direzione',
}

/** Filtro "ruolo d'origine": il ruolo per cui il server ha generato la voce. Vuoto = tutti. */
const SOURCE_ROLES = ['Commerciale', 'CallCenter', 'BackOffice', 'Amministrazione', 'Direzione'] as const

function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type
}

function roleLabel(role: string): string {
  return ROLE_LABELS[role] ?? role
}

/** Dove porta la voce, o null se il tipo di entità non ha una pagina. */
function workQueueLink(item: Pick<WorkQueueItem, 'entityType' | 'entityId'>): string | null {
  switch (item.entityType) {
    case 'contract': return `/contracts/${item.entityId}`
    case 'lead': return `/leads/${item.entityId}`
    case 'contact': return `/contacts/${item.entityId}`
    case 'batch': return '/batches'
    case 'operator_outcome_row': return '/backoffice/outcome-imports'
    case 'task': return '/operations?tab=tasks'
    case 'appointment': return '/operations?tab=appointments'
    case 'invoice': return '/finance'
    case 'financial_movement': return '/finance/bank'
    case 'financial_movement_classification': return '/finance/bank'
    case 'expense': return '/finance/expenses'
    case 'expense_duplicate': return '/finance/controls?tab=duplicates'
    case 'partner': return '/finance/controls?tab=thresholds'
    // F-106: voci aggregate della Direzione e anomalie economiche dell'Amministrazione.
    case 'contract_exception_cause': return '/backoffice/queue'
    case 'contract_sla': return '/backoffice/queue'
    case 'target_pace': return '/targets'
    case 'finance_anomaly': return '/finance/controls'
    case 'liquidity_alert': return '/finance/calendar'
    // F-118: errori di sistema.
    case 'job': return '/jobs'
    case 'webhook_delivery': return '/settings/integrations'
    default:
      if (item.entityType.startsWith('budget_superato:')) return '/finance/controls'
      if (item.entityType.startsWith('scadenza_imposta:') || item.entityType.startsWith('scadenza_scaduta:')) {
        return '/finance/calendar'
      }
      return null
  }
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleDateString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Rome',
  })
}

const CONTACT_LINK = (id: number) => `/contacts/${id}`

/**
 * Azioni rapide di una pratica in coda. Riusano gli stessi endpoint della scheda pratica
 * (`PATCH /api/contracts/{id}/status`, con le transizioni calcolate dal server): nessuna scrittura su portali esterni.
 */
function ContractActions({ item, onChanged }: { item: WorkQueueItem; onChanged: () => void }) {
  const prompt = usePrompt()
  const c = item.contract!
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [transitions, setTransitions] = useState<string[] | null>(null)

  async function change(newStatus: string) {
    const reason = await prompt({
      title: 'Cambia lo stato della pratica',
      message: 'Motivo del cambio di stato (opzionale).',
      label: 'Motivo',
      initialValue: '',
      multiline: true,
      confirmLabel: 'Conferma',
    })
    if (reason === null) return
    setBusy(true)
    setErr(null)
    try {
      await contractsApi.updateStatus(item.entityId, newStatus, reason || undefined)
      setTransitions(null)
      onChanged()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Errore nell’aggiornamento dello stato.')
    } finally {
      setBusy(false)
    }
  }

  async function openTransitions() {
    setBusy(true)
    setErr(null)
    try {
      const full = await contractsApi.get(item.entityId)
      setTransitions(full.availableStatusTransitions ?? [])
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Impossibile leggere gli stati disponibili.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="d-flex flex-wrap gap-1 align-items-center">
      <Link to={`/contracts/${item.entityId}`}>Apri pratica</Link>
      {(c.companyId || c.contactId) && (
        <Link to={CONTACT_LINK((c.contactId ?? c.companyId)!)} className="ms-2">Apri cliente</Link>
      )}
      {c.status === 'DaVerificare' && (
        <Button type="button" size="sm" variant="outlined" disabled={busy} onClick={() => void change('Verificata')}>
          Segna verificata
        </Button>
      )}
      {transitions === null ? (
        <Button type="button" size="sm" variant="text" disabled={busy} onClick={() => void openTransitions()}>
          Cambia stato
        </Button>
      ) : transitions.length === 0 ? (
        <span className="md-typescale-body-small text-muted">Nessuna transizione</span>
      ) : (
        transitions.map(t => (
          <Button key={t} type="button" size="sm" variant="outlined" disabled={busy} onClick={() => void change(t)}>
            {CONTRACT_STATUS_LABELS[t] ?? t}
          </Button>
        ))
      )}
      {err && <div className="md-typescale-body-small text-danger w-100">{err}</div>}
    </div>
  )
}

function WorkQueueRow({ item, onChanged }: { item: WorkQueueItem; onChanged: () => void }) {
  const link = workQueueLink(item)
  const c = item.contract ?? null
  if (c) {
    return (
      <tr>
        <td>
          <Link to={`/contracts/${item.entityId}`}>{c.referenceCode}</Link>
          {item.isStale && <StatusPill tone="warning" className="ms-2">ferma</StatusPill>}
        </td>
        <td>{c.customerName ?? <span className="text-muted">—</span>}</td>
        <td>{c.operatorName ?? <span className="text-muted">—</span>}</td>
        <td>{c.productName ?? <span className="text-muted">—</span>}</td>
        <td>{CONTRACT_STATUS_LABELS[c.status] ?? c.status}</td>
        <td>{c.signedAt ? fmtDate(c.signedAt) : '—'}</td>
        <td>
          {c.reason}
          {c.missing.length > 0 && (
            <div className="md-typescale-body-small text-muted">Mancano: {c.missing.join(', ')}</div>
          )}
        </td>
        <td>{c.waitingDays} {c.waitingDays === 1 ? 'giorno' : 'giorni'}</td>
        <td><ContractActions item={item} onChanged={onChanged} /></td>
      </tr>
    )
  }
  return (
    <tr>
      <td colSpan={2}>
        {link ? <Link to={link}>{item.title}</Link> : item.title}
        {item.isStale && <StatusPill tone="warning" className="ms-2">ferma</StatusPill>}
        {item.needsHuman === false && <StatusPill tone="primary" className="ms-2">automatizzabile</StatusPill>}
        {item.automationHint && (
          <div className="md-typescale-body-small">{item.automationHint}</div>
        )}
      </td>
      <td>{roleLabel(item.sourceRole)}</td>
      <td colSpan={2}>{typeLabel(item.type)}</td>
      <td>{item.dueAt ? fmtDate(item.dueAt) : '—'}</td>
      <td>
        <details>
          <summary>
            <StatusPill tone="primary">{item.score}</StatusPill>
          </summary>
          <ul className="md-typescale-body-small mb-0 ps-3">
            {item.explanation.map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </details>
      </td>
      <td></td>
      <td>{link && <Link to={link}>Apri</Link>}</td>
    </tr>
  )
}

export function WorkQueuePage() {
  // F-118: la prima pagina e' `data`; le successive si accodano a `items` ("Carica altre", con nextOffset).
  const [data, setData] = useState<WorkQueueResponse | null>(null)
  const [items, setItems] = useState<WorkQueueItem[]>([])
  const [filter, setFilter] = useState('')
  const [sourceRole, setSourceRole] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    let alive = true
    void fetchMe()
      .then(me => { if (alive) setIsAdmin(me !== null && (me.role === 'Administrator' || me.role === 'EqualAdmin')) })
      .catch(() => { /* senza sessione leggibile il filtro Errori resta nascosto */ })
    return () => { alive = false }
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await myWorkQueueApi.list(filter || undefined, undefined, sourceRole || undefined)
      setData(res)
      setItems(res.items)
    } catch (e) {
      setData(null)
      setItems([])
      setError(e instanceof Error ? e.message : 'Impossibile caricare la coda di lavoro.')
    } finally {
      setLoading(false)
    }
  }, [filter, sourceRole])

  useEffect(() => { void load() }, [load])

  async function loadMore() {
    if (!data || data.nextOffset === null || loadingMore) return
    setLoadingMore(true)
    setError(null)
    try {
      const res = await myWorkQueueApi.list(filter || undefined, undefined, sourceRole || undefined, data.nextOffset)
      setItems(prev => {
        const seen = new Set(prev.map(i => `${i.entityType}:${i.entityId}:${i.type}`))
        return [...prev, ...res.items.filter(i => !seen.has(`${i.entityType}:${i.entityId}:${i.type}`))]
      })
      setData({ ...res, items: [] })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile caricare altre voci.')
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <Page>
      <PageHeader
        title="La mia coda"
        icon="checklist"
        actions={
          <Button type="button" variant="outlined" onClick={() => void load()} disabled={loading}>
            Aggiorna
          </Button>
        }
      />

      <Callout tone="info" role="note">
        Qui trovi quello che spetta al tuo ruolo, dal più urgente. Il punteggio (0–100) somma urgenza,
        scadenza, probabilità e anzianità: aprilo per vedere come è stato calcolato. Una pratica &quot;ferma&quot;
        è senza cambi di stato da più di 48 ore; le bozze non sono mai ferme.
      </Callout>

      <FilterBar>
        <Select
          id="wq-filter"
          label="Tipo"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        >
          <option value="">Tutti</option>
          <option value={STALE_FILTER}>Solo pratiche ferme</option>
          {Object.entries(TYPE_LABELS)
            .filter(([value]) => value !== ERROR_TYPE)
            .map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          {isAdmin && <option value={ERROR_TYPE}>Errori</option>}
        </Select>
        <Select
          id="wq-source-role"
          label="Ruolo d’origine"
          value={sourceRole}
          onChange={e => setSourceRole(e.target.value)}
        >
          <option value="">Tutti</option>
          {SOURCE_ROLES.map(r => (
            <option key={r} value={r}>{roleLabel(r)}</option>
          ))}
        </Select>
      </FilterBar>

      {error && (
        <Callout
          tone="error"
          role="alert"
          action={<Button type="button" variant="text" onClick={() => void load()}>Riprova</Button>}
        >
          {error}
        </Callout>
      )}

      {isAdmin && <PriorityWeightsPanel />}

      <div aria-busy={loading}>
        {loading ? (
          <LoadingState label="Caricamento della coda..." />
        ) : error && !data ? null : !data ? null : items.length === 0 ? (
          <EmptyState
            icon="checklist"
            title={data.roles.length === 0
              ? 'Per il tuo ruolo non c’è una coda di lavoro.'
              : 'Non c’è niente da lavorare in questo momento.'}
          />
        ) : (
          <>
            <div className="md-typescale-body-small text-muted mb-2">
              {data.roles.length > 0 && <>Ruoli: {data.roles.map(roleLabel).join(', ')}. </>}
              {data.hasMore
                ? `Mostrate ${items.length} voci su ${data.total}.`
                : `${data.total} ${data.total === 1 ? 'voce' : 'voci'}.`}
            </div>
            <DataTable stickyHeader>
              <thead>
                <tr>
                  <th>Codice / voce</th>
                  <th>Cliente</th>
                  <th>Operatore</th>
                  <th>Prodotto</th>
                  <th>Stato</th>
                  <th>Data firma</th>
                  <th>Motivo</th>
                  <th>Attesa</th>
                  <th>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <WorkQueueRow key={`${item.entityType}:${item.entityId}:${item.type}`} item={item} onChanged={() => void load()} />
                ))}
              </tbody>
            </DataTable>
            {data.nextOffset !== null && (
              <div className="text-center my-3">
                <Button
                  type="button"
                  variant="outlined"
                  size="sm"
                  onClick={() => void loadMore()}
                  disabled={loadingMore}
                >
                  {loadingMore ? 'Caricamento...' : `Carica altre (${data.total - items.length} rimaste)`}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Page>
  )
}
